import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, StarIcon, BookOpenIcon } from 'lucide-react';
import { AfterTideBook } from '../../data/aftertideTypes';
import { abbreviateGenre } from '../../utils/genreAbbreviations';
interface AfterTideBookCardProps {
  book: AfterTideBook;
}
// Genre color — exact match with UnifiedBookCard
const getGenreColor = (genre: string): string => {
  const g = genre.toLowerCase();
  if (g.includes('romance')) return 'bg-pink-600';
  if (g.includes('fantasy')) return 'bg-indigo-600';
  if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
  if (g.includes('mystery')) return 'bg-indigo-600';
  if (g.includes('thriller')) return 'bg-red-600';
  if (g.includes('horror')) return 'bg-gray-800';
  if (g.includes('historical')) return 'bg-amber-600';
  if (g.includes('young adult') || g.includes('ya')) return 'bg-cyan-600';
  if (g.includes('contemporary')) return 'bg-emerald-600';
  if (g.includes('erotica')) return 'bg-rose-700';
  if (g.includes('cyberpunk')) return 'bg-blue-600';
  if (g.includes('gothic')) return 'bg-gray-700';
  return 'bg-violet-600';
};
// Status color — exact match with UnifiedBookCard
const getStatusColor = (isNew: boolean, isDebut?: boolean): string => {
  if (isDebut) return 'bg-fuchsia-600';
  if (isNew) return 'bg-indigo-500';
  return 'bg-blue-600';
};
// Derive "new" from publish date (within 90 days)
const checkIsNew = (publishDate: string): boolean => {
  const pub = new Date(publishDate);
  const now = new Date();
  const diffDays = (now.getTime() - pub.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 90;
};
export const AfterTideBookCard: React.FC<AfterTideBookCardProps> = ({
  book
}) => {
  const [imgError, setImgError] = useState(false);
  // Derived values
  const isNew = checkIsNew(book.publishDate);
  const allWarnings = book.contentWarnings.flatMap(w => w.items);
  const displayWarnings = allWarnings.slice(0, 3);
  const hasMoreWarnings = allWarnings.length > 3;
  const authorSlug = book.authorSlug || book.author.toLowerCase().replace(/\s+/g, '-');
  const seriesSlug = book.series?.name.toLowerCase().replace(/\s+/g, '-');
  // Genre tag label — Use structured displayLabel if available, otherwise fallback
  const genreLabel = book.displayLabel || book.genres?.[0] || '';
  const colorGenre = book.primaryGenre || genreLabel;
  // Status label
  const statusLabel = isNew ? 'NEW' : null;
  // Links
  const detailLink = `/aftertide/book/${book.id}`;
  const authorLink = `/aftertide/author/${authorSlug}`;
  const seriesLink = seriesSlug ? `/aftertide/series/${seriesSlug}` : '';
  const reviewsLink = `/aftertide/book/${book.id}/reviews`;
  return <div className="relative group w-full h-full" data-id="element-681" data-mp="24kd4c456b1ns">
      <Link to={detailLink} className="block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-all duration-300 border border-transparent group-hover:border-[#e2b04a]/50 group-hover:shadow-[0_0_15px_rgba(226,176,74,0.15)]" data-id="element-682" data-mp="24jjvmxmnm230">
        {/* Cover Image */}
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#2d1b3d] to-[#1a1a2e]" data-id="element-683" data-mp="35jhqgudgahrr">
            <BookIcon className="h-10 w-10 text-[#e2b04a]/50 mb-2" data-id="element-684" data-mp="3eha31qrsfbbg" />
            <span className="text-[#e2b04a] text-xs font-medium text-center px-2 line-clamp-2" data-id="element-685" data-mp="3ek61dg7js20f">
              {book.title}
            </span>
          </div> : <img src={book.cover} alt={book.title} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-686" data-mp="3ofqzv4deqk5f" />}

        {/* ===== COVER FACE BADGES (always visible, matches UnifiedBookCard exactly) ===== */}

        {/* Genre/Subgenre tag — top left */}
        {genreLabel && <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(colorGenre)} px-2 py-0.5 rounded-sm z-10`} data-id="element-687" data-mp="b2nxfjuwbf5a">
            {abbreviateGenre(genreLabel)}
          </span>}

        {/* Status badge — top right (NEW / DEBUT) */}
        {statusLabel && <span className={`absolute top-2 right-2 text-white text-xs ${getStatusColor(isNew)} px-2 py-0.5 rounded-sm z-10`} data-id="element-688" data-mp="17ryfwb2gt4m9">
            {statusLabel}
          </span>}

        {/* ===== HOVER OVERLAY (AfterTide dark theme with gold accents) ===== */}
        <div className="absolute inset-0 bg-[#1a1a2e]/90 backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover:opacity-100" data-id="element-689" data-mp="tthzfsesvzqx">
          <div className="min-w-0" data-id="element-690" data-mp="12twp13yss108">
            {/* Title */}
            <h3 className="font-bold text-white text-sm line-clamp-2 mb-1 hover:text-[#e2b04a] transition-colors" data-id="element-691" data-mp="12r231804y84p">
              {book.title}
            </h3>

            {/* Author */}
            <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-692" data-mp="2eamxktqp14x0">
              by{' '}
              <Link to={authorLink} className="hover:underline transition-colors hover:text-[#e2b04a]" onClick={e => e.stopPropagation()} data-id="element-693" data-mp="2a6oz8l96zy50">
                {book.author}
              </Link>
            </p>

            {/* Series */}
            {book.series && <div className="text-[11px] flex items-center mb-1.5 text-[#e2b04a]/80" data-id="element-694" data-mp="2cremoe33ul1p">
                <BookOpenIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-695" data-mp="2ct7rroy2kx7l" />
                <Link to={seriesLink} className="line-clamp-1 transition-colors hover:text-[#e2b04a]" onClick={e => e.stopPropagation()} data-id="element-696" data-mp="30hh9aeo92ysx">
                  {book.series.name}
                  {book.series.number ? ` #${book.series.number}` : ''}
                </Link>
              </div>}

            {/* Identity Tags: Pairing + Tropes */}
            <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-697" data-mp="3je0amhstbiex">
              {book.pairingType && <span className="px-1.5 py-0.5 rounded-sm text-[9px] bg-[#1a1a2e] border border-[#e2b04a]/30 text-[#e2b04a]" data-id="element-698" data-mp="3o8ed145w6pt3">
                  {book.pairingType}
                </span>}
              {book.tropes?.slice(0, 2).map((trope, i) => <span key={`trope-${i}`} className="px-1.5 py-0.5 rounded-sm text-[9px] bg-[#1a1a2e] border border-[#e2b04a]/30 text-[#e2b04a]" data-id="element-699" data-mp="3p0j2vfhylrul">
                  {trope}
                </span>)}
            </div>

            {/* Content Warnings */}
            {allWarnings.length > 0 && <div className="flex items-center mb-1.5 flex-wrap gap-1" data-id="element-700" data-mp="35dcxp2amb45q">
                {displayWarnings.map((warning, i) => <span key={`cw-${i}`} className="px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 rounded-sm text-[9px]" data-id="element-701" data-mp="31dkkude6ybuk" data-mpi={i}>
                    {warning}
                  </span>)}
                {hasMoreWarnings && <span className="px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 rounded-sm text-[9px]" data-id="element-702" data-mp="16vj60fseg0r7">
                    +{allWarnings.length - 3}
                  </span>}
              </div>}

            {/* Spice & Burn */}
            <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-703" data-mp="1kusojm42v6yc">
              {book.spiceLevel !== undefined && book.spiceLevel > 0 && <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-sm font-medium bg-rose-500/30 text-rose-200" data-id="element-704" data-mp="o3gnt0040ceu">
                  🌶️ {book.spiceLevel}/5
                </span>}
              {book.burnLevel !== undefined && book.burnLevel > 0 && <span className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-sm font-medium bg-orange-500/30 text-orange-200" data-id="element-705" data-mp="11y99kg6leotv">
                  🔥 {book.burnLevel}/3
                </span>}
            </div>
          </div>

          {/* Bottom: Rating */}
          <div className="mt-auto flex items-center justify-between" data-id="element-706" data-mp="2yzife3io5qap">
            <Link to={reviewsLink} className="flex items-center gap-1 transition-colors hover:text-[#e2b04a]" onClick={e => e.stopPropagation()} data-id="element-707" data-mp="2u0fhd313wkc8">
              <StarIcon className="h-4 w-4 text-amber-400" data-id="element-708" data-mp="33xk4b52s0ju3" />
              <span className="text-[11px]" data-id="element-709" data-mp="3406ab0a0qra7">
                {book.rating > 0 ? book.rating : 'New'}
              </span>
              {book.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-710" data-mp="3cxyscc74kd1a">
                  ({book.reviewCount})
                </span>}
            </Link>
          </div>
        </div>
      </Link>
    </div>;
};